type ReloadableLocation = {
  href?: string;
  reload: () => void;
};

type ReloadGuardStorage = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
};

const reloadGuardKey = 'getapet:chunk-load-recovery';
const reloadGuardDurationMs = 60_000;
const recoveredChunkLoadErrors = new WeakSet<object>();

function getErrorText(error: unknown) {
  if (error instanceof Error) {
    const chunkError = error as Error & { code?: unknown; request?: unknown };

    return `${error.name} ${error.message} ${String(chunkError.code ?? '')} ${String(
      chunkError.request ?? '',
    )}`;
  }

  if (typeof error === 'object' && error !== null) {
    const maybeError = error as {
      code?: unknown;
      message?: unknown;
      name?: unknown;
      request?: unknown;
    };

    return `${String(maybeError.name ?? '')} ${String(maybeError.message ?? '')} ${String(
      maybeError.code ?? '',
    )} ${String(maybeError.request ?? '')}`;
  }

  return String(error ?? '');
}

function getBrowserLocation(): ReloadableLocation | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.location;
}

function getBrowserSessionStorage(): ReloadGuardStorage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function reserveReload(storage: ReloadGuardStorage | null, now: number) {
  if (!storage) {
    return false;
  }

  try {
    const previousReload = Number(storage.getItem(reloadGuardKey));

    if (previousReload > 0 && Math.abs(now - previousReload) < reloadGuardDurationMs) {
      return false;
    }

    storage.setItem(reloadGuardKey, String(now));

    return true;
  } catch {
    return false;
  }
}

export function isChunkLoadError(error: unknown) {
  const text = getErrorText(error);

  return /ChunkLoadError|CSS_CHUNK_LOAD_FAILED|Loading (?:CSS )?chunk \d+ failed|failed to fetch dynamically imported module|Importing a module script failed|\/_next\/static\/(?:chunks|css)\//i.test(
    text,
  );
}

export function wasChunkLoadErrorRecovered(error: unknown) {
  return typeof error === 'object' && error !== null && recoveredChunkLoadErrors.has(error);
}

export function reloadOnChunkLoadError(
  error: unknown,
  location: ReloadableLocation | null = getBrowserLocation(),
  storage: ReloadGuardStorage | null = getBrowserSessionStorage(),
  now = Date.now(),
) {
  if (!location || !isChunkLoadError(error) || !reserveReload(storage, now)) {
    return false;
  }

  if (typeof error === 'object' && error !== null) {
    recoveredChunkLoadErrors.add(error);
  }

  location.reload();

  return true;
}

export function reloadOnRouteLoadError(
  error: unknown,
  location: ReloadableLocation | null = getBrowserLocation(),
  storage: ReloadGuardStorage | null = getBrowserSessionStorage(),
  now = Date.now(),
) {
  if (reloadOnChunkLoadError(error, location, storage, now)) {
    return true;
  }

  if (!location?.href || typeof error !== 'object' || error === null) {
    return false;
  }

  const message = 'message' in error ? error.message : undefined;
  const match =
    typeof message === 'string'
      ? /^Invariant: attempted to hard navigate to the same URL (\/\S*) (https?:\/\/\S+)$/.exec(
          message,
        )
      : null;

  if (!match?.[1] || match[2] !== location.href) {
    return false;
  }

  // Next's failed route-data fallback refuses to navigate to its current URL.
  // Reload only if the browser is still there, using the same guard as chunks.
  try {
    if (new URL(match[1], location.href).href !== location.href) {
      return false;
    }
  } catch {
    return false;
  }

  if (!reserveReload(storage, now)) {
    return false;
  }

  // Keep reporting this error: a reload does not explain why route data failed.
  location.reload();

  return true;
}
